import * as React from 'react';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import {
    buildWebsiteUrlPayload,
    isNoRecordFound,
    seoApi,
    SeoHealthData,
} from 'features/DigitalMarketing/api/seoApi';

const getApiErrorMessage = (error: any, fallback: string) => (
    error?.response?.data?.message || error?.message || fallback
);

export const useSeoHealth = () => {
    const authData = useAuth();
    const [seoData, setSeoData] = React.useState<SeoHealthData | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isAnalysing, setIsAnalysing] = React.useState(false);

    const fetchSeoReport = React.useCallback(async () => {
        if (!authData?.INSTITUTE_ID || !authData?.BRANCH_ID) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        try {
            setSeoData(await seoApi.viewReport());
        } catch (error: any) {
            if (!isNoRecordFound(error)) {
                showGlobalModal(getApiErrorMessage(error, 'Unable to fetch SEO health report'), 'error');
            }
            setSeoData(null);
        } finally {
            setIsLoading(false);
        }
    }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

    React.useEffect(() => {
        fetchSeoReport();
    }, [fetchSeoReport]);

    const analyseSeo = React.useCallback(async (websiteUrl?: string) => {
        if (!authData?.INSTITUTE_ID || !authData?.BRANCH_ID) {
            showGlobalModal('Institute or branch details are missing', 'error');
            return;
        }

        const url = buildWebsiteUrlPayload(websiteUrl || authData?.BRANCH_DOMAIN);

        if (!url) {
            showGlobalModal('Website URL is missing', 'error');
            return;
        }

        setIsAnalysing(true);

        try {
            setSeoData(await seoApi.analyse(url));
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, 'Unable to analyse SEO health'), 'error');
        } finally {
            setIsAnalysing(false);
        }
    }, [authData?.BRANCH_DOMAIN, authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

    return {
        authData,
        seoData,
        isLoading,
        isAnalysing,
        fetchSeoReport,
        analyseSeo,
    };
};
